import {
  RestoredStep01Data,
  RestoredStep01ExternalData,
} from '@features/campaigns/builder/containers/FormPages/FormPages.types';
import { restoreStep01DataDTO } from '@features/campaigns/builder/containers/FormPages/Step01/Step01.dto.ts';
import { restoreStep01ExternalDataDTO } from '@features/campaigns/builder/containers/FormPages/Step01External/Step01External.dto.ts';
import {
  RestoredStep02Data,
  restoreStep02DataDTO,
} from '@features/campaigns/builder/containers/FormPages/Step02/Step02.dto';
import { restoreStep02ThematicPackagesDataDTO } from '@features/campaigns/builder/containers/FormPages/Step02ThematicPackages/Step02ThematicPackages.dto.ts';
import { RestoredStep02ThematicPackagesData } from '@features/campaigns/builder/containers/FormPages/Step02ThematicPackages/Step02ThematicPackages.types.ts';
import {
  RestoredStep03Data,
  restoreStep03DataDTO,
} from '@features/campaigns/builder/containers/FormPages/Step03/Step03.dto.ts';
import { restoreStep03ExternalDataDTO } from '@features/campaigns/builder/containers/FormPages/Step03External/Step03External.dto.ts';
import { RestoredStep03ExternalData } from '@features/campaigns/builder/containers/FormPages/Step03External/Step03External.types.ts';
import { restoreStep03ThematicPackagesDataDTO } from '@features/campaigns/builder/containers/FormPages/Step03ThematicPackages/Step03ThematicPackages.dto.ts';
import { RestoredStep03ThematicPackagesData } from '@features/campaigns/builder/containers/FormPages/Step03ThematicPackages/Step03ThematicPackages.types.ts';
import {
  RestoredStep04Data,
  restoreStep04DataDTO,
} from '@features/campaigns/builder/containers/FormPages/Step04/Step04.dto.ts';
import { ReviewData, reviewDataDTO } from '@features/campaigns/shared/containers/Review/Review.dto.ts';
import {
  CAMPAIGN_BUILDER_STEPS,
  CampaignDetails,
  CampaignMediaTypes,
  CampaignTypes,
  PossibleStepNumber,
} from '@features/campaigns/shared/types/campaign.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';

type StepDataDTO =
  | RestoredStep01Data
  | RestoredStep01ExternalData
  | RestoredStep02Data
  | RestoredStep02ThematicPackagesData
  | RestoredStep03Data
  | RestoredStep03ExternalData
  | RestoredStep03ThematicPackagesData
  | RestoredStep04Data
  | ReviewData;

export function useTransformCampaignDetailsToStepData(
  campaignDetails: CampaignDetails | null | undefined,
  stepNumber: PossibleStepNumber,
) {
  const { isExternalDirectClient, isExternalAgency } = useUserDetails();

  if (!campaignDetails) {
    return;
  }

  const isExternal = isExternalDirectClient || isExternalAgency;
  const isDataForSelfServiceCampaign = isExternal || campaignDetails.type === CampaignTypes.SELF_SERVICE;

  const stepTransformers: Record<PossibleStepNumber, () => StepDataDTO | undefined> = {
    [CAMPAIGN_BUILDER_STEPS.STEP_01]: () =>
      isDataForSelfServiceCampaign
        ? restoreStep01ExternalDataDTO(campaignDetails)
        : restoreStep01DataDTO(campaignDetails),
    [CAMPAIGN_BUILDER_STEPS.STEP_02]: () => {
      if (campaignDetails.mediaType === CampaignMediaTypes.THEMATIC) {
        return restoreStep02ThematicPackagesDataDTO(campaignDetails);
      }

      return restoreStep02DataDTO(campaignDetails);
    },
    [CAMPAIGN_BUILDER_STEPS.STEP_03]: () => {
      if (campaignDetails.mediaType === CampaignMediaTypes.THEMATIC) {
        return restoreStep03ThematicPackagesDataDTO(campaignDetails);
      }

      return isDataForSelfServiceCampaign
        ? restoreStep03ExternalDataDTO(campaignDetails)
        : restoreStep03DataDTO(campaignDetails);
    },
    [CAMPAIGN_BUILDER_STEPS.STEP_04]: () => restoreStep04DataDTO(campaignDetails),
    [CAMPAIGN_BUILDER_STEPS.STEP_05]: () => reviewDataDTO(campaignDetails),
  };

  return stepTransformers[stepNumber]();
}
